import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  AfterViewInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { PaymentService } from "../shared/services/payment.service";
import { PaymentDetails } from "../shared/models/paymentdetails";
import {
  PaymentRequest,
  MakePaymentRes,
} from "../shared/models/makepaymentReq";
import {
  BankPaymentResponse,
  ResponseCodes,
} from "../shared/models/bankPaymentRes";
import { Router, ActivatedRoute } from "@angular/router";
import { TranslationWidth } from "@angular/common";
import { switchMap, takeUntil, tap } from "rxjs/operators";
import { Subject, Subscription, interval, timer } from "rxjs";
import * as util from "../card-processing/card.util";
import {
  CheckoutSettings,
  FinalTransactionStatus,
  WhiteLabelInfo,
} from "src/app/core/models/model";
import { urlShortener } from "src/app/core/constants/constants";
import { StorageService } from "src/app/core/helpers/storage-Service";
import { LoadingService } from "src/app/core/services/loading.service";

@Component({
  selector: "app-bank",
  templateUrl: "./bank.component.html",
  styleUrls: ["./bank.component.css"],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class BankComponent implements OnInit, OnChanges, OnDestroy {
  @Input() paymentReference: string;
  @Input() paymentDetails: PaymentDetails;
  @Output() isMakingPayment = new EventEmitter();
  destroy$: Subject<boolean> = new Subject();

  isLoading: boolean = false;

  isProcessing: boolean = false;
  selectedBank: string;
  // paymentDetails: PaymentDetails;
  bankPaymentRequest = new PaymentRequest();
  bankPaymentRes = new MakePaymentRes();
  statusCheck: boolean;
  paymentStatus = ["CREATED", "ONGOING", "ABANDONED", "FAILED", "SUCCESS"];
  message: string = "";
  timeSpent: number = 0;
  bankName: string;
  refereshLink: string;

  @Input() accountDetails: any;

  @ViewChild("loadingView", { read: TemplateRef, static: true })
  loadingView: TemplateRef<any>;
  @ViewChild("bankView", { read: TemplateRef, static: true })
  bankView: TemplateRef<any>;

  failedResponse: boolean;
  errorResponse: string;

  show: boolean = false;
  loader: boolean;

  @Input() whiteLabelInfo: WhiteLabelInfo;
  // private apiSubscription: Subscription;

  btnDisabled: boolean;
  checkOutInfo: any;

  showPendingStatus = false;
  showTimeoutMessage = false;
  pendingStatusTitle = "Processing Payment";
  pendingStatusMessage = "";
  timeRemaining = 120;
  timeoutMessage = "";
  // destroy$ = new Subject<void>();

  private countdownSubscription: Subscription;
  private apiSubscription: Subscription;
  constructor(
    private payservice: PaymentService,
    private router: Router,
    private route: ActivatedRoute,
    private ref: ChangeDetectorRef,
    private storage: StorageService,
    private loaderService: LoadingService,
  ) {
    this.refereshLink = this.route.snapshot.params["payRef"];
  }

  ngOnInit() {
    this.getBankDetails();
  }

  ngOnChanges(changes) {
    this.paymentDetails = changes["paymentDetails"].currentValue;
    this.setPaymentDetails(this.paymentDetails);
  }

  refreshPage() {
    this.isProcessing = false;
    this.statusCheck = false;
    this.isMakingPayment.emit("");
  }

  setPaymentDetails(details) {
    this.bankPaymentRequest.reference = details.referenceId;
    this.bankPaymentRequest.clientId = details.clientId;
    this.bankPaymentRequest.userId = details.userId;
    this.bankPaymentRequest.amount = details.amount;
    this.bankPaymentRequest.currency = details.currency;
    this.bankPaymentRequest.callbackUrl = details.notificationUrl;
    this.bankPaymentRequest.bankCode = this.selectedBank;
    this.bankPaymentRequest.paymentUrl = details.paymentUrl;
  }

  async getBankDetails() {
    let request = {
      reference: this.paymentDetails.referenceId,
      amount: this.paymentDetails.amount,
      customerId: this.paymentDetails["metadata"].email
        ? this.paymentDetails["metadata"].email
        : this.bankPaymentRequest.userId,
      customerName: this.paymentDetails["metadata"].customerName,
    };
    // let payload = await util.encryptData(request);
    this.loaderService.startLoading();

    this.payservice
      .payWithTransfer(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loaderService.stopLoading();
          // let response = res["encryptedResponse"];
          // response = JSON.parse(response);

          if (res.responseCode === ResponseCodes.success) {
            this.isMakingPayment.emit("bank");
            // this.failedResponse = false;
            this.accountDetails = res;
          }
          if (res.responseCode === ResponseCodes.error) {
            this.failedResponse = true;
            this.errorResponse = res.responseDescription;
          }
          if (res.responseCode === ResponseCodes.pendingResponse) {
            this.accountDetails = res;
          }
        },
        error: (err) => {
          this.loaderService.stopLoading();
          this.failedResponse = true;
          this.errorResponse = err.error.responseMessage;
        },
      });
  }

  confirmTransfer() {
    this.startApiCalls();
  }

  private startApiCalls() {
    this.showPendingState();
    this.isMakingPayment.emit("bank");
    this.btnDisabled = true;

    const payload = {
      transactionReference: this.paymentDetails.referenceId,
    };

    const POLLING_INTERVAL = 30000; // 30s
    const MAX_POLLING_DURATION = 120000; // 2 mins
    const startTime = Date.now();

    // Update countdown every second
    this.countdownSubscription = timer(0, 1000)
      .pipe(takeUntil(timer(MAX_POLLING_DURATION)), takeUntil(this.destroy$))
      .subscribe(() => {
        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);

        this.updatePendingMessage(elapsedSeconds);
      });

    // Poll API every 30 seconds
    this.apiSubscription = timer(0, POLLING_INTERVAL)
      .pipe(
        takeUntil(
          timer(MAX_POLLING_DURATION).pipe(
            tap(() => this.handlePollingTimeout()),
          ),
        ),
        switchMap(() => this.payservice.confirmTransfer(payload, true)),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (response) => {
          if (response.responseCode === ResponseCodes.success) {
            this.stopPolling();
            this.handleSuccessfulPayment(response);
          } else if (response.responseCode === ResponseCodes.error) {
            this.stopPolling();
            this.handleFailedPayment(response);
          } else if (response.responseCode === ResponseCodes.pendingResponse) {
            this.showPendingState();
          }
        },
        (error) => {
          this.stopPolling();
          this.loaderService.stopLoading();
          this.handleApiError(error);
        },
      );
  }

  private updatePendingMessage(elapsedSeconds: number) {
    const messageData = {
      title: "",
      message: "",
      timeRemaining: Math.max(0, 120 - elapsedSeconds),
    };

    if (elapsedSeconds < 30) {
      messageData.title = "Processing Payment";
      messageData.message =
        "We are currently waiting for confirmation from your bank or payment provider. Please keep this page open while we verify your transaction.";
    } else if (elapsedSeconds < 60) {
      messageData.title = "Still Confirming Your Payment";
      messageData.message =
        "This is taking slightly longer than usual, but your transaction is still being verified.";
    } else if (elapsedSeconds < 90) {
      messageData.title = "Verifying Payment";
      messageData.message =
        "We are still checking the status of your payment. Some payment providers require additional time to send confirmation.";
    } else {
      messageData.title = "Almost There";
      messageData.message =
        "Our system is actively checking for an update. Please continue to wait while we retrieve the final status.";
    }

    this.updatePendingScreenMessage(messageData);
  }

  private updatePendingScreenMessage(messageData: any) {
    this.pendingStatusTitle = messageData.title;
    this.pendingStatusMessage = messageData.message;
    this.timeRemaining = messageData.timeRemaining;
    this.ref.detectChanges();
  }

  private stopPolling() {
    this.apiSubscription?.unsubscribe();
    this.countdownSubscription?.unsubscribe();
  }

  private showPendingState() {
    // this.loaderService.startLoading();
    this.showPendingStatus = true;
  }

  private handleSuccessfulPayment(response: any) {
    this.loaderService.stopLoading();
    this.stopApiCalls();

    const status: FinalTransactionStatus = {
      amount: response.amount,
      status: "success",
      currency: this.paymentDetails.currency,
      callbackUrl: urlShortener(this.paymentDetails.callbackURL),
    };

    this.router.navigateByUrl(`/status/${btoa(JSON.stringify(status))}`);
  }

  private handleFailedPayment(response: any) {
    this.loaderService.stopLoading();
    this.stopApiCalls();

    const status: FinalTransactionStatus = {
      amount: response.amount,
      status: "failed",
      currency: this.paymentDetails.currency,
      callbackUrl: urlShortener(this.paymentDetails.callbackURL),
    };

    this.router.navigateByUrl(`/status/${btoa(JSON.stringify(status))}`);
  }

  private handlePollingTimeout() {
    this.loaderService.stopLoading();
    this.stopApiCalls();
    this.showPendingStatus = false;
    this.showTimeoutMessage = true;
    this.timeoutMessage =
      "We could not retrieve the final payment status at this time. You may check your transaction status later or contact support if the status remains unchanged.";
  }

  private handleApiError(error: any) {
    this.stopApiCalls();
    this.showPendingStatus = false;

    // Handle network/API errors - show error toast or redirect
  }

  private stopApiCalls() {
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe();
    }
    this.btnDisabled = false;
  }

  // private stopApiCalls() {
  //   if (this.apiSubscription) {
  //     this.apiSubscription.unsubscribe();
  //   }
  // }

  showStatus() {
    let contxt: any = this;
    this.show = true;
    setTimeout(function () {
      contxt.show = false;
      this.message = "hello";
      return;
    }, 3500);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
